/**
 * Core API client — the v2 surfaces
 *
 * Talks to /api/core. Deliberately thin: it shapes nothing and hides nothing.
 *
 * The one thing it does do is keep a refusal a refusal. The resolver returns
 * HTTP 200 with `refused: true` when it will not report a figure — the request
 * succeeded, the answer is "we will not guess". Flattening that into an error
 * would throw away the message and the remedy, which are the most useful part
 * of the response and the whole reason the behaviour exists.
 */

import { api } from '../../services/api';

export type CertificationState =
  | 'draft'
  | 'proposed'
  | 'certified'
  | 'disputed'
  | 'retired';

export interface MetricSummary {
  key: string;
  label: string;
  description: string;
  unit: 'days' | 'currency' | 'percent' | 'count' | 'ratio';
  grain: string;
  authoritativeSource: string;
  version: number;
  certificationState: CertificationState;
  owner: string | null;
  steward: string | null;
  certifiedAt: string | null;
  disputeReason: string | null;
  /** The formula rendered in English, so the register is reviewable. */
  explanation: string;
}

export interface LineageRow {
  entity: string;
  sourceId: string | number;
  reference: string | null;
  documentDate: string | null;
  contribution: number | null;
}

export interface LineageStep {
  description: string;
  rowCount: number;
  value: number | null;
  sampleRows: LineageRow[];
}

export interface ConflictReport {
  sourceId: string;
  label: string;
  value: number;
  difference: number;
  differencePercent: number;
  withinTolerance: boolean;
  windowMisaligned: boolean;
  knownDifferences: string[];
  note: string;
}

export interface ResolvedMetric {
  key: string;
  label: string;
  unit: MetricSummary['unit'];
  value: number;
  currency: string | null;
  referenceDate: string;
  definitionVersion: number;
  certificationState: CertificationState;
  owner: string | null;
  authoritativeSource: string;
  lineage: LineageStep[];
  rowsConsidered: number;
  resolvedAt: string;
  computeMs: number;
  warnings: string[];
  conflicts: ConflictReport[];
}

export interface MetricRefusal {
  key: string;
  refused: true;
  reason:
    | 'not_defined'
    | 'not_certified'
    | 'disputed'
    | 'no_source'
    | 'invalid_definition';
  message: string;
  /** What someone would have to do to get a number. */
  remedy: string;
}

export type MetricResult = ResolvedMetric | MetricRefusal;

export function isRefusal(result: MetricResult): result is MetricRefusal {
  return (result as MetricRefusal).refused === true;
}

/** Format a resolved value the way the unit implies. */
export function formatMetric(
  value: number,
  unit: MetricSummary['unit'],
  currency: string | null
): string {
  switch (unit) {
    case 'currency':
      return `${currency ?? ''} ${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`.trim();
    case 'days':
      return `${value.toLocaleString()} days`;
    case 'percent':
      return `${value.toLocaleString()}%`;
    case 'count':
      return value.toLocaleString();
    default:
      return String(value);
  }
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

class CoreService {
  /** The metric register, with each definition rendered in English. */
  async listMetrics(): Promise<MetricSummary[]> {
    const response = await api.get<Envelope<MetricSummary[]>>('/core/metrics');
    return response.data ?? [];
  }

  /**
   * Resolve one metric. Returns a refusal rather than throwing when the
   * system declines to report a figure.
   */
  async resolveMetric(key: string, referenceDate?: string): Promise<MetricResult> {
    const query = referenceDate ? `?referenceDate=${encodeURIComponent(referenceDate)}` : '';
    const response = await api.get<Envelope<MetricResult>>(`/core/metrics/${key}${query}`);
    return response.data;
  }

  /** The finance owner agreeing that this is how the number is counted. */
  async certifyMetric(key: string, ownerLabel: string, note?: string): Promise<void> {
    await api.post(`/core/metrics/${key}/certify`, { ownerLabel, note });
  }

  /** Anyone who can see a number may say it is wrong. */
  async disputeMetric(key: string, reason: string): Promise<void> {
    await api.post(`/core/metrics/${key}/dispute`, { reason });
  }

  /** Load the Finance starter definitions, as proposals. */
  async installCatalog(): Promise<void> {
    await api.post('/core/metrics/install-catalog', {});
  }
}

export const coreService = new CoreService();
